import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, effect, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import {
  CachedBuildingService,
  DeleteLocalCacheCategory,
  ENVIRONMENT,
  PageSectionBuildingModule,
  SharedService
} from '@cfp/core';
import { BuildingDetailsHeader } from '@cfp/features-shared/building';
import { LabelImprovementInline } from '@cfp/features-shared/label-improvement-inline';
import { BuildingLayoutData } from '@cfp/shared/building/layout';
import { RouteUtility } from '@cfp/shared/router';
import { LayoutContainer } from '@cfp/shared/ui/layout-container';
import { NavService, NavbarItem, TabbarItem } from '@cfp/shared/ui/nav';
import { ErrorService } from '@cfp/ui/services';
import { BuildingDetailsDTO, LabelGroupDTO, RegisteredOrCalculatedEnergyLabelDTO } from 'csr-asset-app';
import { Observable, Subscription, combineLatest, map, of, startWith, switchMap, tap } from 'rxjs';

@Component({
  selector: 'cfp-building-layout',
  templateUrl: './building-layout.html',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [LayoutContainer, BuildingDetailsHeader, PageSectionBuildingModule, RouterModule, LabelImprovementInline],
  providers: [DatePipe]
})
export class BuildingLayout implements OnInit, OnDestroy {
  protected navbar = signal<NavbarItem[] | undefined>(undefined);
  protected tabbar = computed(() => this.buildingLayoutDataTabbar() || this.innerTabbar());

  private innerTabbar = signal<TabbarItem[] | undefined>(undefined);
  private reloadComponentSubscription: Subscription;
  private deleteLocalCacheSubscription: Subscription;

  private readonly sharedService = inject(SharedService);
  private readonly navService = inject(NavService);
  private readonly cachedBuildingService = inject(CachedBuildingService);
  private readonly errorService = inject(ErrorService);
  private readonly environment = inject(ENVIRONMENT);
  private readonly routeUtility = inject(RouteUtility);

  protected readonly showInlineLabelImprovement = this.environment.showInlineLabelImprovement;
  protected readonly showDownloadReportButton =
    this.environment.components.hideReportFromNavAndShowButtonOnBuildingPage === true;

  protected readonly portfolioId = toSignal(
    this.routeUtility.getAllRouteParams().pipe(
      map((params) => {
        const portfolioId = params['portfolioId'];
        return portfolioId ? parseInt(portfolioId) : undefined;
      })
    ),
    { initialValue: undefined }
  );

  protected readonly buildingId = toSignal(
    this.routeUtility.getAllRouteParams().pipe(
      map((params) => {
        const buildingId = params['buildingId'];
        return buildingId ? parseInt(buildingId) : undefined;
      })
    ),
    { initialValue: undefined }
  );

  protected readonly hideBuildingDetailsHeader = toSignal(
    this.routeUtility.getDataFromChildRoutes('hideBuildingDetailsHeader', false),
    { initialValue: false }
  );

  protected buildingLayoutData = computed<BuildingLayoutData | undefined>(() => {
    const buildingId = this.buildingId();
    const building = this.building();
    if (!buildingId || !building) {
      return undefined;
    }
    return {
      portfolioId: this.portfolioId(),
      buildingId: buildingId,
      building: building,
      labelGroup: this.buildingLayoutDatalabelGroup(),
      labelGroups: this.buildingLayoutDataLabelGroups(),
      currentLabel: this.buildingLayoutDataCurrentLabel(),
      tabbar: this.buildingLayoutDataTabbar
    };
  });

  private readonly buildingLayoutDataTabbar = signal<TabbarItem[] | undefined>(undefined);
  private readonly buildingLayoutDatalabelGroup = signal<LabelGroupDTO | undefined>(undefined);
  private readonly buildingLayoutDataLabelGroups = signal<LabelGroupDTO[] | undefined>(undefined);
  private readonly buildingLayoutDataCurrentLabel = signal<RegisteredOrCalculatedEnergyLabelDTO | undefined>(undefined);

  private reloadTrigger = this.sharedService.reloadComponentObservable.pipe(
    map(() => new Date().getTime()) // Force a new value on each emission
  );

  protected readonly building = toSignal(
    combineLatest([toObservable(this.buildingId), this.reloadTrigger.pipe(startWith(null))]).pipe(
      switchMap(([buildingId]) => {
        if (!buildingId) {
          return of(undefined);
        }
        return this.getBuilding(buildingId);
      })
    )
  );

  constructor() {
    effect(() => {
      const portfolioId = this.portfolioId();
      const buildingId = this.buildingId();
      if (portfolioId && buildingId) {
        this.loadNavbar(portfolioId);
        this.loadTabbar(portfolioId, buildingId);
      }
    });
  }

  public ngOnInit(): void {
    window.scrollTo(0, 0);

    this.reloadComponentSubscription = this.sharedService.reloadComponentObservable.subscribe(() => {
      const portfolioId = this.portfolioId();
      const buildingId = this.buildingId();
      if (!portfolioId || !buildingId) {
        return;
      }
      this.loadNavbar(portfolioId);
      this.loadTabbar(portfolioId, buildingId);
    });

    this.deleteLocalCacheSubscription = this.sharedService.deleteLocalCacheObservable.subscribe(
      (category: DeleteLocalCacheCategory) => {
        const portfolioId = this.portfolioId();
        const buildingId = this.buildingId();
        if (!portfolioId || !buildingId) {
          return;
        }
        if (category === DeleteLocalCacheCategory.Notification) {
          this.loadNavbar(portfolioId);
          this.loadTabbar(portfolioId, buildingId);
        }
      }
    );
  }

  public ngOnDestroy(): void {
    if (this.reloadComponentSubscription) {
      this.reloadComponentSubscription.unsubscribe();
    }

    if (this.deleteLocalCacheSubscription) {
      this.deleteLocalCacheSubscription.unsubscribe();
    }
  }

  protected setLabelGroup(labelGroup: LabelGroupDTO): void {
    this.buildingLayoutDatalabelGroup.set(labelGroup);
  }

  protected setLabelGroups(labelGroups: LabelGroupDTO[]): void {
    this.buildingLayoutDataLabelGroups.set(labelGroups);
  }

  protected setCurrentLabel(currentLabel: RegisteredOrCalculatedEnergyLabelDTO): void {
    this.buildingLayoutDataCurrentLabel.set(currentLabel);
  }

  private loadNavbar(portfolioId: number): void {
    this.navService
      .getNavbar('energy', {
        portfolioId: portfolioId
      })
      .subscribe((navbar: NavbarItem[]) => {
        this.navbar.set(navbar);
      });
  }

  private loadTabbar(portfolioId: number, buildingId: number): void {
    this.navService
      .getTabbar('building', {
        portfolioId: portfolioId,
        buildingId: buildingId
      })
      .subscribe((tabbar: TabbarItem[]) => {
        this.innerTabbar.set(tabbar);
      });
  }

  private getBuilding(buildingId: number): Observable<BuildingDetailsDTO> {
    return this.cachedBuildingService.v2BuildingsIdGet(buildingId).pipe(
      tap({
        error: () => this.errorService.rerouteAfterError()
      })
    );
  }
}
